import { AuthState, UserRole } from '@/store/auth/types';
import { QueryKeys } from '@/types';
import { getDaysAgoInEpochMilliseconds } from '@/utilities/helpers';
import { isAgentOrAgencyRole, isPartnerRole } from '@/utilities/roles/permissions';
import { FilterKey, FilterKeyMap } from 'api-paths-cas';
import { parseDate, formatDate } from 'shared-components';

export const getUniversityFromParams = (auth: AuthState, params: URLSearchParams): string => {
  const isAgent = isAgentOrAgencyRole(auth.role);
  const isPartner = isPartnerRole(auth.role);

  // agents don't have universities
  if (isAgent || isPartner) return undefined;

  const accessParamUni = [UserRole.Admin, UserRole.Enroly].includes(auth.role);
  const uniParam = params.get(QueryKeys.UNI);
  const localStorageUni = localStorage.getItem('dashboard-uni-institution-value');
  // if we are admin/ enroly we return the uni from params
  if (accessParamUni && (uniParam || localStorageUni)) return uniParam || localStorageUni;
  // All other roles
  return auth.uni;
};

// These keys require epoch ms conversion and are transported separately via getIdleParamsFromParams.
// Skip them here to avoid placing raw day-count strings into the generic filters object.
const NON_GENERIC_FILTER_KEYS = new Set([FilterKey.MINIMUM_DAYS_IDLE, FilterKey.MAXIMUM_DAYS_IDLE]);

/**
 * * Handles pulling filters from params.
 * * Handles convering FilterKey params to ApiFilterKey params via FilterKeyMap.
 *
 * Saga [FilterKey] => getFilterKeysFromParams [ApiFilterKey] => api.ts Local Endpoint [ApplicationPGFilterMap | ApplicantOfferPGFilterMap]
 *
 * @param params URLSearchParams
 */
export const getFilterKeysFromParams = (params: URLSearchParams): Record<string, string[]> => {
  const filters: Record<string, string[]> = {};
  for (const filterKey of Object.values(FilterKey)) {
    if (NON_GENERIC_FILTER_KEYS.has(filterKey)) continue;
    if (params.has(filterKey) && FilterKeyMap[filterKey]) {
      filters[FilterKeyMap[filterKey]] = params.getAll(filterKey);
    }
  }
  return filters;
};

/**
 * Takes a date string in format "YYYY-MM-DD"
 * and returns "YYYY-MM" (year and month only).
 */
export function toYearMonth(date: string): string {
  return formatDate(parseDate(date), 'YYYY-MM');
}

/**
 * Extracts idleFrom/idleTo epoch ms params from URLSearchParams.
 * MAXIMUM_DAYS_IDLE maps to idleFrom (older timestamp boundary),
 * MINIMUM_DAYS_IDLE maps to idleTo (newer timestamp boundary).
 */
export const getIdleParamsFromParams = (
  params: URLSearchParams,
): { idleFrom: number[] | undefined; idleTo: number[] | undefined } => ({
  idleFrom: params.has(FilterKey.MAXIMUM_DAYS_IDLE)
    ? [getDaysAgoInEpochMilliseconds(params.getAll(FilterKey.MAXIMUM_DAYS_IDLE).map(Number)[0])]
    : undefined,
  idleTo: params.has(FilterKey.MINIMUM_DAYS_IDLE)
    ? [getDaysAgoInEpochMilliseconds(params.getAll(FilterKey.MINIMUM_DAYS_IDLE).map(Number)[0]) - 1]
    : undefined,
});
